/**
 * ContactsAttemptsModal — the Contacts tab's drill-down: the attempts behind one clicked bar, day,
 * source, person, funnel step, rating or reason.
 *
 * THE HOUSE DRILL MODAL, NOT A NEW ONE: the Dialog shell, header and footer of
 * `TraceDrillDownModal` and the same `TraceTable` inside it (expandable rows → the full request and
 * answer and the Langfuse link, via `LazyTraceDetail`), with one column added to that table for what
 * the other modals have no need of — the outcome («Imported · 5 sent · 4 created · 1 refused»). The
 * data comes from /api/contacts/attempts, the /traces/list pattern (filters + slice + page), and it
 * is SERVER-paged, with the Prev / Page N of M / Next row of the user page's activity feed.
 */

import * as React from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  TraceTable,
  sortRows,
  type TraceColumnKey,
  type TraceTableRow,
  type TraceTableSort,
  type TraceSortKey,
} from '@/components/TraceTable';
import { useContactsAttempts, type ContactsSlice } from '@/hooks/use-contacts';
import type { Filters } from '@/lib/filters-schema';
import type { ContactsAttempt } from '@/services/api-types';

export const CONTACTS_PAGE_SIZE = 25;

const COLUMNS: TraceColumnKey[] = ['time', 'user', 'role', 'outcome', 'rating', 'queryAnswer', 'feedback'];
const DEFAULT_SORT: TraceTableSort = { key: 'timestamp', dir: 'desc' };

/** What came of one attempt, in one line — the counts and the ending, never a contact's data. */
export function outcomeLine(a: ContactsAttempt, outcomeLabel: (o: string) => string = (o) => o): string {
  const b = a.batch;
  if (!b) return `No card — ${outcomeLabel(a.requestOutcome ?? 'unknown')}`;
  const parts = [b.source];
  if (b.imported) {
    parts.push(`Imported${b.ending && b.ending !== 'done' ? ` (${b.ending})` : ''}`, `${b.rowsSent} sent`, `${b.added} created`, `${b.refused} refused`);
    if (b.unclear > 0) parts.push(`${b.unclear} unclear`);
  } else if (b.cancelled) parts.push('Cancelled', `${b.onCard} on card`);
  else if (b.sent) parts.push('Sending', `${b.rowsSent} sent`);
  else if (b.expired) parts.push('Expired', `${b.onCard} on card`);
  else parts.push('Open', `${b.onCard} on card`);
  if (b.opened) parts.push('fix opened');
  return parts.join(' · ');
}

export function attemptToRow(a: ContactsAttempt, outcomeLabel?: (o: string) => string): TraceTableRow {
  return {
    traceId: a.traceId,
    timestamp: a.timestamp,
    userId: a.userId ?? '',
    realName: a.realName,
    // The turn the row links to is an ordinary coach turn; the name only picks the Kind label and
    // the Q/A cell's rendering, and this table shows no Kind column.
    name: 'sales-coach-request',
    complexity: null,
    rating: a.batch?.rating ?? null,
    latencyMs: null,
    cost: null,
    input: a.input,
    output: a.output,
    error: null,
    textFeedback: a.batch?.textFeedback ?? null,
    isRm: a.isRm,
    outcome: outcomeLine(a, outcomeLabel),
  };
}

export function ContactsAttemptsModal({
  open,
  onClose,
  filters,
  slice,
  title,
  outcomeLabel,
  testIdPrefix = 'contacts-drilldown-modal',
}: {
  open: boolean;
  onClose: () => void;
  filters: Filters;
  slice: ContactsSlice;
  title: string;
  outcomeLabel?: (o: string) => string;
  testIdPrefix?: string;
}): React.ReactElement {
  const [page, setPage] = React.useState(1);
  const [sort, setSort] = React.useState<TraceTableSort>(DEFAULT_SORT);
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(() => new Set());
  const sliceKey = `${slice.kind}:${slice.value ?? ''}`;
  React.useEffect(() => {
    if (open) {
      setPage(1);
      setSort(DEFAULT_SORT);
      setExpandedIds(new Set());
    }
  }, [open, sliceKey]);

  const query = useContactsAttempts(filters, slice, (page - 1) * CONTACTS_PAGE_SIZE, open);
  const rawRows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / CONTACTS_PAGE_SIZE));
  const rows = React.useMemo(() => rawRows.map((a) => attemptToRow(a, outcomeLabel)), [rawRows, outcomeLabel]);
  const sorted = React.useMemo(() => sortRows(rows, sort), [rows, sort]);
  const counting = query.isLoading || !query.data;

  const onSort = React.useCallback((key: TraceSortKey) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'timestamp' || key === 'username' ? 'asc' : 'desc' }));
  }, []);
  const toggle = React.useCallback((traceId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(traceId)) next.delete(traceId);
      else next.add(traceId);
      return next;
    });
  }, []);

  const rangeStart = (page - 1) * CONTACTS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, page * CONTACTS_PAGE_SIZE);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent
        data-testid={testIdPrefix}
        className="max-w-[min(96vw,80rem)] w-[min(96vw,80rem)] max-h-[88vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle data-testid={`${testIdPrefix}-title`}>{title}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Every request and card behind this slice, for the current filters. Click a row for the full request and answer, and its trace.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <TraceTable
            rows={sorted}
            columns={COLUMNS}
            sort={sort}
            onSort={onSort}
            from={filters.from ?? undefined}
            to={filters.to ?? undefined}
            isLoading={query.isLoading}
            isError={query.isError}
            onRetry={() => void query.refetch()}
            emptyMessage="Nothing matches this slice for the current filters."
            testIdPrefix={testIdPrefix}
            expandable
            expandedIds={expandedIds}
            onToggleExpanded={toggle}
          />
        </div>

        {total > 0 && (
          <div
            data-testid={`${testIdPrefix}-pagination`}
            className="flex items-center justify-between border-t border-border pt-2 text-sm"
          >
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || counting}
              data-testid={`${testIdPrefix}-prev`}
              className="px-2 py-1 font-medium text-primary hover:underline disabled:opacity-40 disabled:no-underline"
            >
              ← Prev
            </button>
            <span className="text-xs text-muted-foreground tabular-nums" data-testid={`${testIdPrefix}-range`}>
              Showing {rangeStart}–{rangeEnd} of {total} · Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || counting}
              data-testid={`${testIdPrefix}-next`}
              className="px-2 py-1 font-medium text-primary hover:underline disabled:opacity-40 disabled:no-underline"
            >
              Next →
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
